<template>
    <editor
        v-model="modelValue"
        :api-key="tinyApiKey"
        :init="{
            menu: {
                placeholders: {
                    title: $t('placeholders'),
                    items: placeholderItems,
                },
                ...(documentPlaceholderItems.length
                    ? {
                          documents: {
                              title: $t('documents'),
                              items: documentPlaceholderItems,
                          },
                      }
                    : {}),
            },
            menubar: menubar,
            plugins: plugins,
            fullscreen_native: true,
            toolbar: toolbar,
            fontsize_formats: '8px 10px 12px 14px 16px 18px 24px 36px 48px',
            font_size_input_default_unit: 'px',
            setup: setupTinyMCE,
            language: $i18n.locale,
            content_style: `body { font-size: 14px; }`,
        }"
    />
</template>
<script lang="ts" setup>
import { Placeholder } from '@src/types'
import Editor from '@tinymce/tinymce-vue'
import { useVModels } from '@vueuse/core'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const props = withDefaults(
    defineProps<{
        placeholders?: Placeholder[] | null
        documentPlaceholders?: Placeholder[] | null
        modelValue?: string | null
        variant?: 'full' | 'minimal'
    }>(),
    {
        variant: 'full',
    }
)

const emit = defineEmits(['update:modelValue'])

const placeholderItems: string[] = []
const documentPlaceholderItems: string[] = []

const { modelValue } = useVModels(props, emit)

props.placeholders?.forEach((placeholder: Placeholder) => {
    placeholderItems.push(placeholder.key)
})

props.documentPlaceholders?.forEach((placeholder: Placeholder) => {
    documentPlaceholderItems.push('doc_' + placeholder.key)
})

// load api key from environment file
const tinyApiKey = import.meta.env.VITE_TINY_API_KEY
const tinyPaidPlugins = import.meta.env.VITE_TINY_PAID_PLUGINS

const isMinimal = computed(() => props.variant === 'minimal')

const plugins = computed(() =>
    isMinimal.value
        ? 'lists link'
        : `lists link image code fullscreen${
              tinyPaidPlugins === 'true' ? ' formatpainter advcode' : ''
          }`
)

const toolbar = computed(() =>
    isMinimal.value
        ? 'undo redo | bold italic underline | bullist numlist | link'
        : `undo redo | styleselect | bold italic | fontsizeselect fontselect | removeformat | bullist numlist | link | image | alignleft aligncenter alignright alignjustify | outdent indent |
    ${
        tinyPaidPlugins === 'true'
            ? 'formatpainter formatpainter_removeformat | '
            : ''
    } fullscreen | code`
)

const menubar = computed(() =>
    isMinimal.value
        ? false
        : `edit view format placeholders${
              documentPlaceholderItems.length ? ' documents' : ''
          }`
)

const setupTinyMCE = function (editor: typeof Editor) {
    // render the given placeholders into the menu
    props.placeholders?.forEach((placeholder: Placeholder) => {
        editor.on('PreInit', () => {
            editor.editorManager.addI18n('de', {
                'System Font': 'Schriftart ändern',
            })
            editor.editorManager.addI18n('en', {
                'System Font': 'Change Font',
            })
        })
        editor.ui.registry.addMenuItem(placeholder.key, {
            text: t('insert') + ' ' + placeholder.key,
            onAction: () => {
                editor.insertContent(placeholder.value)
            },
        })
    })

    // render the given document placeholders into their own menu
    props.documentPlaceholders?.forEach((placeholder: Placeholder) => {
        editor.ui.registry.addMenuItem('doc_' + placeholder.key, {
            text: t('insert') + ' ' + placeholder.key,
            onAction: () => {
                editor.insertContent(placeholder.value)
            },
        })
    })
}
</script>
